/**
 * Messages Store - Utils
 * Нормализация данных и вспомогательные функции
 */

import { logger, getCurrentUserId } from '../../utils';
import type {
  NormalisedConversation,
  NormalisedMessage,
  LastMessage,
  UnreadCounterRow,
  MessagesState,
  MessageUpdatePatch,
  MergeMessagesResult,
  DeliveryStatus,
} from './types';

// ===== Temp ID Generation =====

let tempIdCounter = 0;
let lastTimestamp = 0;

export const generateTempId = (): string => {
  const now = Date.now();
  if (now !== lastTimestamp) {
    tempIdCounter = 0;
    lastTimestamp = now;
  }
  tempIdCounter += 1;
  return `temp-${now}-${tempIdCounter}-${Math.random().toString(36).substring(2, 9)}`;
};

// ===== Timestamp Normalization =====

export const normalizeTimestampSeconds = (raw: unknown): number => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return Math.floor(Date.now() / 1000);
  return n > 1e12 ? Math.floor(n / 1000) : n;
};

// ===== Unread Counters =====

export const normalizeUnreadCounters = (
  raw: UnreadCounterRow[] | Record<string, number | UnreadCounterRow> | null | undefined
): Record<string, number> => {
  const out: Record<string, number> = {};
  if (!raw) return out;

  const entries: UnreadCounterRow[] = Array.isArray(raw)
    ? raw
    : (Object.values(raw) as UnreadCounterRow[]);

  entries.forEach((row: UnreadCounterRow) => {
    if (!row) return;
    const contactId = row.contact_id ?? row.contactId ?? row.id;
    const count = row.new_messages_count ?? row.newMessagesCount ?? row.count ?? row.value;
    if (contactId === undefined || contactId === null) return;
    const n = Number(count || 0);
    out[String(contactId)] = Number.isFinite(n) ? n : 0;
  });

  // Fallback for simple map format
  if (!Object.keys(out).length && raw && typeof raw === 'object' && !Array.isArray(raw)) {
    Object.keys(raw).forEach((k) => {
      const v = (raw as any)[k];
      if (typeof v === 'number') {
        out[String(k)] = v;
      }
    });
  }

  return out;
};

export const sumUnread = (map: Record<string, number>): number => {
  let total = 0;
  if (!map) return 0;
  Object.keys(map).forEach((k) => {
    const n = Number(map[k] || 0);
    if (Number.isFinite(n) && n > 0) total += n;
  });
  return total;
};

// ===== Conversation Normalization =====

export const normalizeConversation = (raw: Record<string, unknown>): NormalisedConversation => {
  const c: any = raw && typeof raw === 'object' ? raw : {};
  const contactId = c.contact?.id ?? c.contact_id ?? c.contactId ?? c.contactID ?? c.user_id ?? c.userId;

  const contact = c.contact
    ? c.contact
    : contactId !== undefined && contactId !== null
      ? {
          id: contactId,
          full_name: c.full_name ?? c.fullName ?? c.name ?? c.username,
          avatar: c.avatar ?? c.avatar_url ?? c.avatarUrl ?? c.photo ?? c.photo_url,
          age: c.age,
          online: c.online,
          last_seen: c.last_seen ?? null,
          last_activity: c.last_activity ?? null,
          premium: c.premium,
        }
      : undefined;

  const lastMessage = c.last_message
    ? {
        ...c.last_message,
        id: c.last_message.id ?? c.last_message_id ?? c.lastMessageId,
        text: c.last_message.text ?? c.last_message_text ?? c.lastMessageText ?? c.last_message ?? c.lastMessage ?? c.message,
        timestamp: normalizeTimestampSeconds(
          c.last_message.timestamp ?? c.last_message_at ?? c.lastMessageAt ?? c.last_message_time ?? c.lastMessageTime ?? c.updated_at ?? c.updatedAt
        ),
        hasMedia: c.last_message.hasMedia ?? c.last_message_has_media ?? c.lastMessageHasMedia ?? c.has_media ?? c.hasMedia,
        type: c.last_message.type ?? c.last_message_type ?? c.lastMessageType ?? c.type,
        status: c.last_message.status,
        is_read: c.last_message.is_read,
        fromUserId: c.last_message.fromUserId ?? c.last_message.from_user_id,
        toUserId: c.last_message.toUserId ?? c.last_message.to_user_id,
      }
    : {
        id: c.last_message_id ?? c.lastMessageId,
        text: c.last_message_text ?? c.lastMessageText ?? c.last_message ?? c.lastMessage ?? c.message,
        timestamp: normalizeTimestampSeconds(
          c.last_message_at ?? c.lastMessageAt ?? c.last_message_time ?? c.lastMessageTime ?? c.updated_at ?? c.updatedAt
        ),
        hasMedia: c.last_message_has_media ?? c.lastMessageHasMedia ?? c.has_media ?? c.hasMedia,
        type: c.last_message_type ?? c.lastMessageType ?? c.type ?? 'unknown',
        status: c.status,
        is_read: c.is_read,
        fromUserId: c.from_user_id,
        toUserId: c.to_user_id,
      };

  const out = {
    ...c,
    contact,
    last_message: lastMessage,
  };

  if (contactId !== undefined && contactId !== null) {
    return { ...out, id: contactId } as NormalisedConversation;
  }
  return out as NormalisedConversation;
};

// ===== Message Normalization =====

export const normalizeMessage = (raw: Record<string, unknown>): NormalisedMessage | null => {
  const msg: any = raw && typeof raw === 'object' ? raw : {};
  const data: any = msg.data && typeof msg.data === 'object' ? msg.data : msg;

  const currentUserId = getCurrentUserId();

  const id = data.message_id ?? data.messageId ?? data.id;
  let chatId = data.contactId ?? data.contact_id ?? data.chatId ?? data.chat_id ?? data.conversationId ?? data.conversation_id;
  const fromUserId = data.from_user_id ?? data.fromUserId ?? data.from;
  const toUserId = data.to_user_id ?? data.toUserId ?? data.to;
  const text = data.body ?? data.message ?? data.text ?? '';
  const timestamp = data.created_at ?? data.timestamp ?? data.ts;

  // Attachments handling
  const attachmentsRaw = Array.isArray(data.attachments)
    ? data.attachments
    : Array.isArray(data.mediaFiles)
      ? data.mediaFiles.map((f: any) => ({
          id: f?.id,
          name: f?.name,
          size: f?.size,
          mime: f?.type,
          type: f?.type,
          preview_url: f?.preview,
          thumbnail: f?.preview,
          url: f?.url || f?.preview,
        }))
      : [];

  const attachments = attachmentsRaw.map((a: any) => {
    const mime = a?.mime || a?.type || '';
    const isImage = typeof mime === 'string' && mime.toLowerCase().startsWith('image/');
    return {
      ...a,
      type: isImage ? 'image' : 'file',
      thumbnail: a?.thumbnail || a?.preview_url || a?.previewUrl || null,
      // Preserve objectUrl for local preview during upload
      objectUrl: a?.objectUrl || null,
      _pending: a?._pending || false,
      _uploadProgress: a?._uploadProgress || 0,
    };
  });

  // Infer chatId from participants if missing
  if (!chatId && currentUserId !== null && currentUserId !== undefined && currentUserId !== '') {
    if (fromUserId !== undefined && fromUserId !== null && toUserId !== undefined && toUserId !== null) {
      chatId = String(fromUserId) === String(currentUserId) ? toUserId : fromUserId;
    }
  }

  if (!chatId) return null;

  const type =
    currentUserId !== null && fromUserId !== undefined && fromUserId !== null
      ? String(fromUserId) === String(currentUserId)
        ? 'sent'
        : 'received'
      : data.type || undefined;

  // Status normalization
  let status: string;
  let isRead: boolean;

  // Extract temp_id from various possible field names
  const tempId = data.temp_id ?? data.tempId ?? data.client_message_id ?? data.clientMessageId;

  if (data.status) {
    status = data.status;
    isRead = data.is_read === 1 || data.is_read === true || data.status === 'read';
  } else {
    const isNew = data.is_new ?? data.isNew ?? true;
    isRead = isNew === 0 || isNew === false;
    status = isRead ? 'read' : 'sent';
  }

  // Avatar determination
  let userAvatar = null;
  let userFullName = null;
  if (data.user?.avatar) {
    userAvatar = data.user.avatar;
  } else if (data.avatar || data.avatar_url || data.avatarUrl) {
    userAvatar = data.avatar ?? data.avatar_url ?? data.avatarUrl;
  } else if (data.photo || data.photo_url) {
    userAvatar = data.photo ?? data.photo_url;
  } else if (data.from_avatar || data.to_avatar) {
    userAvatar = data.from_avatar ?? data.to_avatar;
  } else if (data.contact?.avatar) {
    userAvatar = data.contact.avatar;
  }
  
  // Also check from_user object (for confirmation messages)
  if (data.from_user?.avatar) {
    userAvatar = data.from_user.avatar;
  }
  
  // Full name determination
  if (data.user?.full_name || data.user?.name || data.user?.username) {
    userFullName = data.user.full_name ?? data.user.name ?? data.user.username;
  } else if (data.contact?.full_name || data.contact?.name) {
    userFullName = data.contact.full_name ?? data.contact.name;
  } else if (data.from_user?.display_name || data.from_user?.full_name || data.from_user?.name) {
    userFullName = data.from_user.display_name ?? data.from_user.full_name ?? data.from_user.name;
  } else if (data.from_user_name || data.display_name) {
    userFullName = data.from_user_name ?? data.display_name;
  }

  return {
    ...data,
    id: id ?? `msg-${Date.now()}`,
    chatId,
    contactId: chatId, // Preserve contactId for consistency
    text,
    timestamp: normalizeTimestampSeconds(timestamp),
    ts: normalizeTimestampSeconds(timestamp),
    from_user_id: fromUserId,
    type,
    status,
    is_read: isRead,
    delivered_at: data.delivered_at,
    read_at: data.read_at,
    temp_id: tempId,
    attachments,
    user: {
      id: fromUserId || currentUserId,
      avatar: userAvatar,
      full_name: userFullName,
      name: userFullName,
      display_name: userFullName,
    },
  } as NormalisedMessage;
};

// ===== Merge & Dedup Logic =====

export const mergeMessagesWithDedup = (
  existing: NormalisedMessage[],
  normalized: NormalisedMessage[]
): MergeMessagesResult => {
  const updates: MessageUpdatePatch[] = [];
  const currentUserId = getCurrentUserId();

  logger.debug('[MERGE] Input:', {
    existingCount: existing.length,
    normalizedCount: normalized.length,
    existingTempMessages: existing.filter(m => {
      const idStr = String(m?.id ?? '');
      return idStr.startsWith('temp-') || idStr.startsWith('temp_');
    }).map(m => ({ id: m.id, _serverId: m._serverId, status: m.status })),
  });

  const existingOrder = new Map<string, number>();
  existing.forEach((m, idx) => {
    const key = String(m?.id ?? '');
    if (key) existingOrder.set(key, idx);
  });

  // Match server messages to local temp media
  const matchedServerIds = new Set<string>();
  const matchedTempIds = new Set<string>(); // Track matched temp IDs to exclude from keep

  try {
    const optimisticMedia = existing.filter((m) => {
      const idStr = String(m?.id ?? '');
      const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
      const hasAtt = Array.isArray(m?.attachments) && m.attachments.length > 0;
      return isTemp && hasAtt;
    });

    logger.log('mergeMessagesWithDedup: optimisticMedia count:', optimisticMedia.length);
    optimisticMedia.forEach((m) => {
      logger.log('mergeMessagesWithDedup: temp message:', {
        id: m.id,
        timestamp: m.timestamp,
        text: m.text,
        attachments: m.attachments?.map((a: any) => ({ name: a.name, size: a.size })),
      });
    });

    const optimisticMediaByServerId = new Map<string, NormalisedMessage>();
    optimisticMedia.forEach((m) => {
      const sid = m?._serverId;
      if (sid !== undefined && sid !== null) {
        optimisticMediaByServerId.set(String(sid), m);
      }
    });

    // Match own sent server messages to optimistic media
    normalized.forEach((srv) => {
      const hasServerId = typeof srv?.id === 'number' || (typeof srv?.id === 'string' && /^\d+$/.test(srv.id));
      const srvTempIdRaw = srv?.temp_id ?? srv?.tempId ?? srv?.client_message_id;
      const hasAnyTempId = srvTempIdRaw !== undefined && srvTempIdRaw !== null;
      const isOwn = currentUserId !== null && String(srv?.from_user_id) === String(currentUserId) && srv?.type === 'sent';

      logger.log('mergeMessagesWithDedup: checking server message:', {
        id: srv.id,
        hasServerId,
        hasAnyTempId,
        isOwn,
        timestamp: srv.timestamp,
        text: srv.text,
        attachments: srv.attachments?.map((a: any) => ({ name: a.name, size: a.size })),
      });

      if (!isOwn || !hasServerId || hasAnyTempId) {
        logger.log('mergeMessagesWithDedup: skipping server message (filters)');
        return;
      }

      const incomingTs = Number(srv?.timestamp ?? srv?.ts ?? 0);
      const incomingAttachments = Array.isArray(srv?.attachments) ? srv.attachments : [];
      const srvIdStr = String(srv.id);

      // Exact match by _serverId
      const exactTemp = optimisticMediaByServerId.get(srvIdStr);
      if (exactTemp) {
        const tempId = String(exactTemp.id);
        matchedServerIds.add(srvIdStr);
        matchedTempIds.add(tempId); // Track matched temp ID
        updates.push({
          tempId,
          patch: {
            ...srv,
            id: tempId,
            _serverId: srv.id,
            status: srv.status || 'sent',
            _uploadComplete: true,
            _backgroundUploaded: true,
            _backgroundUploadStarted: false,
          },
        });
        logger.log('mergeMessagesWithDedup: exact match by _serverId');
        return;
      }

      // Fuzzy match by content
      const candidates = optimisticMedia.filter((m) => !m?._serverId);
      logger.log('mergeMessagesWithDedup: fuzzy candidates count:', candidates.length);
      
      let bestCandidate: NormalisedMessage | null = null;
      let bestTsDiff = Number.POSITIVE_INFINITY;

      candidates.forEach((m) => {
        // Match by text (if both have text)
        const textMatch = String(m?.text ?? '') === String(srv?.text ?? '');
        
        const ts = Number(m?.timestamp ?? m?.ts ?? 0);
        const diff = incomingTs > 0 && ts > 0 ? Math.abs(incomingTs - ts) : 0;
        
        // Increased tolerance: 300 seconds (5 minutes) for media uploads
        if (incomingTs > 0 && ts > 0 && diff > 300) {
          logger.log('mergeMessagesWithDedup: candidate rejected - timestamp diff too large:', diff);
          return;
        }

        const atts = Array.isArray(m?.attachments) ? m.attachments : [];
        if (incomingAttachments.length !== atts.length) {
          logger.log('mergeMessagesWithDedup: candidate rejected - attachment count mismatch');
          return;
        }
        
        // Match by attachment names and sizes
        let attachmentsMatch = true;
        for (let i = 0; i < incomingAttachments.length; i++) {
          const a = incomingAttachments[i] || {};
          const b = atts[i] || {};
          if (a.name && b.name && String(a.name) !== String(b.name)) attachmentsMatch = false;
          if (a.size && b.size && Number(a.size) !== Number(b.size)) attachmentsMatch = false;
        }
        
        // For media messages, prioritize attachments match over text match
        const isMediaMatch = incomingAttachments.length > 0 && atts.length > 0 && attachmentsMatch;
        
        logger.log('mergeMessagesWithDedup: candidate check:', {
          tempId: m.id,
          textMatch,
          isMediaMatch,
          attachmentsMatch,
          diff,
        });
        
        if (!textMatch && !isMediaMatch) return;

        if (diff < bestTsDiff) {
          bestCandidate = m;
          bestTsDiff = diff;
        }
      });

      if (bestCandidate) {
        const tempId = String((bestCandidate as NormalisedMessage).id);
        matchedServerIds.add(srvIdStr);
        matchedTempIds.add(tempId); // Track matched temp ID
        updates.push({
          tempId,
          patch: {
            ...srv,
            id: tempId,
            _serverId: srv.id,
            status: srv.status || 'sent',
            _uploadComplete: true,
            _backgroundUploaded: true,
            _backgroundUploadStarted: false,
          },
        });
        logger.log('mergeMessagesWithDedup: fuzzy match found:', { tempId, serverId: srv.id });
      } else {
        logger.log('mergeMessagesWithDedup: no fuzzy match found for server message:', srv.id);
      }
    });

    // Match by temp_id / client_message_id
    normalized.forEach((srv) => {
      const hasServerId = typeof srv?.id === 'number' || (typeof srv?.id === 'string' && /^\d+$/.test(srv.id));
      if (!hasServerId) return;
      const srvTempIdRaw = srv?.temp_id ?? srv?.tempId ?? srv?.client_message_id;
      if (srvTempIdRaw === undefined || srvTempIdRaw === null) return;
      const srvTempId = String(srvTempIdRaw);

      const existingTemp = existing.find((m) => String(m?.id ?? '') === srvTempId);
      if (!existingTemp) return;

      matchedServerIds.add(String(srv.id));
      matchedTempIds.add(srvTempId); // Track matched temp ID
      updates.push({
        tempId: srvTempId,
        patch: {
          ...srv,
          id: srvTempId,
          _serverId: srv.id,
          status: srv.status || 'sent',
          _uploadComplete: true,
          _backgroundUploaded: true,
          _backgroundUploadStarted: false,
        },
      });
    });

    // Match TEXT messages by _serverId (for HTTP fallback when no client_message_id in response)
    // Build map of all temp messages (not just media) by _serverId
    const allTempMessagesByServerId = new Map<string, NormalisedMessage>();
    existing.forEach((m) => {
      const idStr = String(m?.id ?? '');
      const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
      const sid = m?._serverId;
      if (isTemp && sid !== undefined && sid !== null) {
        allTempMessagesByServerId.set(String(sid), m);
        logger.log('mergeMessagesWithDedup: registered temp with _serverId:', { tempId: idStr, serverId: String(sid) });
      }
    });

    normalized.forEach((srv) => {
      const srvIdStr = String(srv.id);
      
      // Skip if already matched
      if (matchedServerIds.has(srvIdStr)) {
        logger.log('mergeMessagesWithDedup: server message already matched:', srvIdStr);
        return;
      }
      
      // Skip media messages (handled above)
      const hasAttachments = Array.isArray(srv?.attachments) && srv.attachments.length > 0;
      if (hasAttachments) return;

      // Look for temp text message with matching _serverId
      const matchingTemp = allTempMessagesByServerId.get(srvIdStr);
      logger.log('mergeMessagesWithDedup: looking for match for server msg:', srvIdStr, 'found:', matchingTemp ? matchingTemp.id : 'none', 'candidates:', Array.from(allTempMessagesByServerId.keys()));
      if (matchingTemp) {
        const tempId = String(matchingTemp.id);
        matchedServerIds.add(srvIdStr);
        matchedTempIds.add(tempId); // Track matched temp ID
        updates.push({
          tempId,
          patch: {
            ...srv,
            id: tempId,  // Keep temp ID for UI stability
            _serverId: srv.id,
            status: srv.status || 'sent',
            _uploadComplete: true,
          },
        });
        logger.log('mergeMessagesWithDedup: text message matched by _serverId:', { tempId, serverId: srv.id });
      }
    });
  } catch {
    // ignore
  }

  // Filter out matched server messages
  const normalizedFiltered = normalized.filter((m) => !matchedServerIds.has(String(m?.id ?? '')));

  // Preserve optimistic/local messages not yet returned by server
  // NOTE: matched temp messages are NOT excluded here - they need to stay in the array
  // so that updateMessage can find and update them with server data. The server message
  // is already filtered out via matchedServerIds, so no duplicates will occur.
  const optimisticToKeep = existing.filter((m) => {
    const idStr = String(m?.id ?? '');
    
    const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
    const hasAtt = Array.isArray(m?.attachments) && m.attachments.length > 0;
    const isInFlight = m?.status === 'pending' || m?.status === 'uploading' || m?.status === 'sending' || m?._backgroundUploadStarted;
    const isJustCompletedMedia = isTemp && hasAtt && (m?._uploadComplete || m?._backgroundUploaded);
    const hasServerId = m?._serverId !== undefined && m?._serverId !== null;
    const isConfirmedTempMedia = isTemp && hasAtt && hasServerId;
    // KEEP: confirmed text messages with _serverId (HTTP fallback mode)
    const isDeliveredTextWithServerId = isTemp && !hasAtt && hasServerId && (m?.status === 'delivered' || m?.status === 'sent');
    return Boolean(isInFlight || isJustCompletedMedia || isConfirmedTempMedia || isDeliveredTextWithServerId);
  });

  const normalizedIds = new Set(normalizedFiltered.map((m) => String(m?.id ?? '')));
  const normalizedTempIds = new Set(
    normalizedFiltered
      .map((m) => m?.temp_id ?? m?.tempId ?? m?.client_message_id)
      .filter((x): x is string => x !== undefined && x !== null)
      .map((x) => String(x))
  );

  const keep = optimisticToKeep.filter((m) => {
    const idStr = String(m?.id ?? '');
    return !normalizedIds.has(idStr) && !normalizedTempIds.has(idStr);
  });

  const keepIds = new Set(keep.map((m) => String(m?.id ?? '')));

  // Preserve existing non-temp messages missing from server response
  const missingExisting = existing.filter((m) => {
    const idStr = String(m?.id ?? '');
    if (normalizedIds.has(idStr) || normalizedTempIds.has(idStr) || keepIds.has(idStr)) return false;
    const isTemp = idStr.startsWith('temp-') || idStr.startsWith('temp_');
    return !isTemp;
  });

  missingExisting.sort((a, b) => {
    const ta = Number(a?.timestamp ?? a?.ts ?? 0);
    const tb = Number(b?.timestamp ?? b?.ts ?? 0);
    if (ta && tb) return ta - tb;
    return 0;
  });

  // Merge and sort chronologically
  const merged = [...missingExisting, ...normalizedFiltered, ...keep];

  merged.sort((a, b) => {
    const ta = Number(a?.timestamp ?? a?.ts ?? 0);
    const tb = Number(b?.timestamp ?? b?.ts ?? 0);
    if (ta && tb && ta !== tb) return ta - tb;
    if (ta && !tb) return 1;
    if (!ta && tb) return -1;

    const oa = existingOrder.get(String(a?.id ?? ''));
    const ob = existingOrder.get(String(b?.id ?? ''));
    if (oa !== undefined && ob !== undefined && oa !== ob) return oa - ob;
    if (oa !== undefined && ob === undefined) return -1;
    if (oa === undefined && ob !== undefined) return 1;

    const ida = String(a?.id ?? '');
    const idb = String(b?.id ?? '');
    return ida.localeCompare(idb);
  });

  return { merged, updates };
};

// ===== UI Helpers =====

export const updateMessagesHeaderBadge = (unreadMap: Record<string, number>): void => {
  const total = sumUnread(unreadMap);
  const w = window as any;

  try {
    if (typeof w.updateNavigationBadge === 'function') {
      w.updateNavigationBadge('messages', total);
      return;
    }
    if (w.NavigationManagerInstance?.updateCounterAbsolute) {
      w.NavigationManagerInstance.updateCounterAbsolute('messages', total);
      return;
    }
  } catch {
    // ignore
  }

  // DOM fallback
  try {
    const el = document.querySelector('[data-nav-indicator="messages"]') as HTMLElement | null;
    if (!el) return;
    el.textContent = total > 99 ? '99+' : String(Math.max(0, total));
    if (total > 0) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', 'hidden');
    }
    el.setAttribute('data-nav-count', String(total));
  } catch {
    // ignore
  }
};

export const updateConversationOrder = (store: MessagesState, convKey: string, lastMessage: LastMessage): void => {
  const orderIdx = (store.conversationOrder || []).findIndex(
    (c: NormalisedConversation) => String(c?.id ?? c?.contact?.id) === convKey
  );
  if (orderIdx >= 0) {
    store.conversationOrder[orderIdx] = { ...store.conversationOrder[orderIdx], last_message: lastMessage };
    store.conversationOrder = [...store.conversationOrder];
  }
};

export const updateConversationAndMoveToTop = (
  store: MessagesState,
  chatId: string | number,
  normalized: NormalisedMessage
): void => {
  const convKey = String(chatId);
  const existingConv = store.conversations[convKey];
  const hasMedia = Array.isArray(normalized.attachments) && normalized.attachments.length > 0;

  logger.log('Store: updateConversationAndMoveToTop', {
    chatId,
    hasMedia,
    attachmentsCount: normalized.attachments?.length || 0,
    textLength: normalized.text?.length || 0,
    messageType: normalized.type,
    existingConv: !!existingConv,
  });

  // Build contact data from message if this is a new conversation
  let contactData = existingConv?.contact;
  if (!contactData && normalized.user) {
    // Extract contact info from the message's user data
    contactData = {
      id: chatId,
      full_name: normalized.user.full_name ?? normalized.user.name ?? normalized.user.username ?? '',
      avatar: normalized.user.avatar ?? normalized.user.avatar_url ?? normalized.user.photo_url ?? null,
      age: normalized.user.age,
      online: normalized.user.online,
      last_seen: normalized.user.last_seen ?? null,
      last_activity: normalized.user.last_activity ?? null,
      premium: normalized.user.premium,
    };
    logger.log('Store: Created contact data from message user', { chatId, contact: contactData });
  }

  const updatedConv = {
    ...(existingConv || { id: chatId, contact: contactData }),
    // Ensure id matches contact.id for consistent Vue :key binding
    id: chatId,
    contact: contactData ? { ...contactData, id: chatId } : existingConv?.contact,
    last_message: {
      ...(existingConv?.last_message || {}),
      text: normalized.text,
      timestamp: normalized.timestamp ?? normalized.ts,
      hasMedia,
      type: normalized.type,
      status: normalized.status,
      is_read: normalized.is_read,
    },
  };
  store.conversations[convKey] = updatedConv;

  // Persist conversation update to IndexedDB for offline consistency
  if (typeof window !== 'undefined' && (window as any).unifiedDBv2?.put) {
    try {
      void (window as any).unifiedDBv2.put('conversations', {
        ...updatedConv,
        id: convKey,
        lastMessageAt: updatedConv.last_message?.timestamp || Date.now() / 1000,
        updatedAt: Date.now(),
        cachedAt: Date.now(),
      });
    } catch (e) {
      // Silent fail for cache update
    }
  }

  // Move to top - ГАРАНТИРУЕМ отсутствие дубликатов по ID контакта
  const currentList = Array.isArray(store.conversationOrder) ? store.conversationOrder : [];
  
  // Ищем по contact.id или по id самого conversation
  const idx = currentList.findIndex((c: NormalisedConversation) => {
    const cId = String(c?.id ?? c?.contact?.id ?? '');
    return cId === convKey;
  });
  
  if (idx >= 0) {
    // Запись существует - обновляем и перемещаем в начало
    const existingContact = currentList[idx].contact;
    const mergedContact = contactData || existingContact;
    const row = { 
      ...currentList[idx],
      ...updatedConv,
      contact: mergedContact
    };
    
    // Создаем новый массив без этой записи
    const nextList = currentList.filter((_, i) => i !== idx);
    // Добавляем в начало
    nextList.unshift(row);
    store.conversationOrder = nextList;
    logger.log('Store: Moved existing conversation to top', { chatId, idx });
  } else {
    // Запись не существует - добавляем новую в начало
    // Дополнительная проверка на дубликаты по contact.id
    const duplicateIdx = currentList.findIndex((c: NormalisedConversation) => {
      const cContactId = String(c?.contact?.id ?? '');
      return cContactId === convKey;
    });
    
    if (duplicateIdx >= 0) {
      // Дубликат найден по contact.id - обновляем и перемещаем в начало
      const existingContact = currentList[duplicateIdx].contact;
      const mergedContact = contactData || existingContact;
      const row = { 
        ...currentList[duplicateIdx],
        ...updatedConv,
        contact: mergedContact
      };
      
      const nextList = currentList.filter((_, i) => i !== duplicateIdx);
      nextList.unshift(row);
      store.conversationOrder = nextList;
      logger.log('Store: Merged duplicate conversation by contact.id', { chatId, duplicateIdx });
    } else {
      // Настоящий новый контакт - добавляем в начало
      const nextList = [updatedConv, ...currentList];
      store.conversationOrder = nextList;
      logger.log('Store: Added new conversation to top', { chatId });
    }
  }
};

// ===== Delivery Status =====

const STATUS_RANK: Record<string, number> = {
  pending: 0,
  sending: 0,
  uploading: 0,
  sent: 1,
  delivered: 2,
  read: 3,
};

/**
 * Не позволяет понизить статус сообщения (read > delivered > sent > pending).
 * Защищает от гонки: событие message_read может прийти раньше подтверждения
 * отправки или эха сервера, которые несут устаревший статус.
 */
export const pickHigherStatus = (current?: string | null, incoming?: string | null): string | undefined => {
  if (!incoming) return current ?? undefined;
  if (incoming === 'failed') return incoming;

  const c = STATUS_RANK[String(current)] ?? -1;
  const i = STATUS_RANK[String(incoming)] ?? -1;

  if (i < 0) return current ?? undefined;
  if (c < 0) return incoming;
  return i >= c ? incoming : (current ?? undefined);
};

export const checkMessageDeliveryStatus = (message: Partial<NormalisedMessage>): DeliveryStatus => {
  if (!message) return { status: 'sent', is_read: false };

  if (message.read_at && message.read_at > 0) {
    return {
      status: 'read',
      is_read: true,
      read_at: message.read_at,
      delivered_at: message.delivered_at,
    };
  }

  if (message.delivered_at && message.delivered_at > 0) {
    return {
      status: 'delivered',
      is_read: false,
      delivered_at: message.delivered_at,
    };
  }

  if (message.status === 'read' || message.is_read === true) {
    return {
      status: 'read',
      is_read: true,
      read_at: message.read_at,
      delivered_at: message.delivered_at,
    };
  }

  if (message.status === 'delivered') {
    return {
      status: 'delivered',
      is_read: false,
      delivered_at: message.delivered_at,
    };
  }

  return { status: 'sent', is_read: false };
};
